import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Phone, RefreshCw, Search, Upload } from "lucide-react";
import { prospectsApi } from "@/api";
import type {
  ProspectFreshness,
  ProspectIndustryFitValue,
  ProspectVerificationStatus,
} from "@/types";

const freshnessLabels: Record<ProspectFreshness, string> = {
  FRESH: "新しい",
  AGING: "経時",
  STALE: "古い",
};

const freshnessColors: Record<ProspectFreshness, string> = {
  FRESH: "bg-green-100 text-green-700",
  AGING: "bg-amber-100 text-amber-700",
  STALE: "bg-slate-100 text-slate-700",
};

const verificationLabels: Record<ProspectVerificationStatus, string> = {
  UNVERIFIED: "未確認",
  VERIFIED: "確認済み",
  UNREACHABLE: "不通",
  REFUSED: "拒否",
  INVALID: "無効",
};

const verificationColors: Record<ProspectVerificationStatus, string> = {
  UNVERIFIED: "bg-slate-100 text-slate-700",
  VERIFIED: "bg-green-100 text-green-700",
  UNREACHABLE: "bg-amber-100 text-amber-700",
  REFUSED: "bg-red-100 text-red-700",
  INVALID: "bg-slate-200 text-slate-700",
};

const industryFitLabels: Record<ProspectIndustryFitValue, string> = {
  HIGH: "高適合",
  MEDIUM: "中適合",
  LOW: "低適合",
  EXCLUDE: "除外",
  UNKNOWN: "不明",
};

function compactDate(value: string | null) {
  if (!value) return "-";
  return format(new Date(value), "yyyy/MM/dd");
}

export default function Prospects() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const csvFileRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [companySearch, setCompanySearch] = useState("");
  const [listSource, setListSource] = useState("");
  const [listVersion, setListVersion] = useState("");
  const [freshnessStatus, setFreshnessStatus] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [industryFit, setIndustryFit] = useState("");
  const [prefecture, setPrefecture] = useState("");
  const [hasLastOrder, setHasLastOrder] = useState<"" | "true" | "false">("");
  const [untouchedOnly, setUntouchedOnly] = useState(false);
  const [hasPhoneOnly, setHasPhoneOnly] = useState(true);
  const [staleDays, setStaleDays] = useState("365");
  const [readyPreset, setReadyPreset] = useState<"CALL" | "FAX" | "MASTER" | "ALL">("ALL");
  const [importPurpose, setImportPurpose] = useState<"EXECUTABLE" | "MASTER">("EXECUTABLE");

  const [importListSource, setImportListSource] = useState("townpage_refined");
  const [importListVersion, setImportListVersion] = useState(new Date().toISOString().slice(0, 10));
  const [importMode, setImportMode] = useState<"append" | "refresh">("append");
  const [dryRun, setDryRun] = useState(false);
  const [importResult, setImportResult] = useState<{
    mode: "append" | "refresh";
    dryRun: boolean;
    listSource: string;
    listVersion: string;
    created: number;
    updated: number;
    skipped: number;
    linked: number;
    total: number;
    matchedBy: Record<string, number>;
    errors: string[];
  } | null>(null);

  const listParams = useMemo(() => {
    const params: Record<string, string> = {
      page: String(page),
      limit: "50",
    };
    if (companySearch.trim()) params.companySearch = companySearch.trim();
    if (listSource.trim()) params.listSource = listSource.trim();
    if (listVersion.trim()) params.listVersion = listVersion.trim();
    if (freshnessStatus) params.freshnessStatus = freshnessStatus;
    if (verificationStatus) params.verificationStatus = verificationStatus;
    if (industryFit) params.industryFit = industryFit;
    if (prefecture.trim()) params.prefecture = prefecture.trim();
    if (hasLastOrder) params.hasLastOrder = hasLastOrder;
    if (untouchedOnly) params.outgoingCallCountMax = "0";
    if (hasPhoneOnly) params.hasPhone = "true";
    if (readyPreset === "CALL") params.readyForCall = "true";
    if (readyPreset === "FAX") params.readyForFax = "true";
    if (readyPreset === "MASTER") params.masterOnly = "true";
    return params;
  }, [companySearch, freshnessStatus, hasLastOrder, hasPhoneOnly, industryFit, listSource, listVersion, page, prefecture, readyPreset, untouchedOnly, verificationStatus]);

  const { data, isLoading } = useQuery({
    queryKey: ["prospects", listParams],
    queryFn: () => prospectsApi.list(listParams),
  });

  const { data: refreshCandidates } = useQuery({
    queryKey: ["prospect-refresh-candidates", staleDays],
    queryFn: () => prospectsApi.refreshCandidates({ staleDays }),
  });

  const { data: executableSummary } = useQuery({
    queryKey: ["prospect-executable-summary"],
    queryFn: () => prospectsApi.executableSummary(),
  });

  const { data: callQueue } = useQuery({
    queryKey: ["prospect-call-queue"],
    queryFn: () => prospectsApi.callQueue({ limit: "20" }),
  });

  const reconcileMutation = useMutation({
    mutationFn: () =>
      prospectsApi.reconcile({
        limit: 500,
        ...(listSource.trim() ? { listSource: listSource.trim() } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] });
    },
  });

  const resolvedImportSource = useMemo(() => {
    const raw = importListSource.trim() || (importPurpose === "EXECUTABLE" ? "trader_list" : "gbiz_basic");
    if (raw.startsWith("exec_") || raw.startsWith("master_")) return raw;
    return `${importPurpose === "EXECUTABLE" ? "exec_" : "master_"}${raw}`;
  }, [importListSource, importPurpose]);

  const launchProspectCallCampaign = () => {
    const params = new URLSearchParams({ audienceType: "PROSPECT" });
    if (listSource.trim()) params.set("listSource", listSource.trim());
    if (listVersion.trim()) params.set("listVersion", listVersion.trim());
    if (freshnessStatus) params.set("freshnessStatus", freshnessStatus);
    if (verificationStatus) params.set("verificationStatus", verificationStatus);
    if (industryFit) params.set("industryFit", industryFit);
    if (prefecture.trim()) params.set("prefecture", prefecture.trim());
    if (hasLastOrder) params.set("hasLastOrder", hasLastOrder);
    if (untouchedOnly) params.set("outgoingCallCountMax", "0");
    if (hasPhoneOnly) params.set("hasPhone", "true");
    if (companySearch.trim()) params.set("companySearch", companySearch.trim());
    navigate(`/call-campaigns/new?${params.toString()}`);
  };

  const applyAiCallPocPreset = () => {
    setCompanySearch("");
    setListSource("core_traders");
    setListVersion("");
    setFreshnessStatus("");
    setVerificationStatus("UNVERIFIED");
    setIndustryFit("HIGH,MEDIUM");
    setPrefecture("茨城県");
    setHasLastOrder("false");
    setUntouchedOnly(true);
    setHasPhoneOnly(true);
    setReadyPreset("ALL");
    setPage(1);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">見込みリスト</h1>
          <p className="text-sm text-slate-500 mt-1">既存母集団の掘り起こしと、新規リスト追加を同じ画面で回します。</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={applyAiCallPocPreset}
            className="px-4 py-2 border rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            AI架電POC候補を適用
          </button>
          <button
            onClick={launchProspectCallCampaign}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Phone size={18} />
            この条件で架電キャンペーン作成
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-4">
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">リスト追加・更新</h2>
            <button
              onClick={() => csvFileRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50"
            >
              <Upload size={16} />
              CSVを選択
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">リストソース</label>
              <input
                type="text"
                value={importListSource}
                onChange={(e) => setImportListSource(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="townpage_refined"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">リストバージョン</label>
              <input
                type="text"
                value={importListVersion}
                onChange={(e) => setImportListVersion(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="2026-03-08"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="radio"
                checked={importPurpose === "EXECUTABLE"}
                onChange={() => setImportPurpose("EXECUTABLE")}
              />
              実行リスト
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="radio"
                checked={importPurpose === "MASTER"}
                onChange={() => setImportPurpose("MASTER")}
              />
              マスター
            </label>
            <span className="text-xs text-slate-500">保存名: {resolvedImportSource}</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="radio"
                checked={importMode === "append"}
                onChange={() => setImportMode("append")}
              />
              新規追加
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="radio"
                checked={importMode === "refresh"}
                onChange={() => setImportMode("refresh")}
              />
              旧リスト更新
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Dry Run
            </label>
          </div>
          <p className="text-xs text-slate-500">
            `append` は新規母集団の追加、`refresh` は同一母集団の再精査結果を上書き統合します。
          </p>
          <input
            ref={csvFileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              e.target.value = "";
              setImportResult(null);
                  try {
                    const result = await prospectsApi.importCsv(file, {
                  listSource: resolvedImportSource,
                  listVersion: importListVersion.trim() || new Date().toISOString().slice(0, 10),
                  mode: importMode,
                  dryRun,
                });
                setImportResult(result);
                if (!result.dryRun) {
                  queryClient.invalidateQueries({ queryKey: ["prospects"] });
                  queryClient.invalidateQueries({ queryKey: ["prospect-refresh-candidates"] });
                  queryClient.invalidateQueries({ queryKey: ["prospect-call-queue"] });
                }
              } catch (err: any) {
                alert(err.message || "インポートに失敗しました");
              }
            }}
          />
          {importResult && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm">
              <p>
                {importResult.dryRun ? "Dry Run 完了" : "取込完了"}:
                作成 <strong className="text-green-700">{importResult.created}</strong> /
                更新 <strong className="text-blue-700">{importResult.updated}</strong> /
                連携 <strong>{importResult.linked}</strong> /
                スキップ <strong>{importResult.skipped}</strong> /
                合計 <strong>{importResult.total}</strong>
              </p>
              <p className="text-xs text-slate-600 mt-1">
                {importResult.listSource} / {importResult.listVersion} / {importResult.mode}
              </p>
              {importResult.errors.length > 0 && (
                <div className="mt-2 text-xs text-red-600">
                  {importResult.errors.slice(0, 5).map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">旧リストの再連携</h2>
            <button
              onClick={() => reconcileMutation.mutate()}
              disabled={reconcileMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw size={16} className={reconcileMutation.isPending ? "animate-spin" : ""} />
              {reconcileMutation.isPending ? "連携中..." : "contact再突合"}
            </button>
          </div>
          <p className="text-xs text-slate-500">
            既存コンタクトへの紐付けをやり直し、重複接点を避けます。
          </p>
          {reconcileMutation.data && (
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <p>対象 {reconcileMutation.data.total} 件</p>
              <p>linked {reconcileMutation.data.linked} / noMatch {reconcileMutation.data.noMatch} / conflict {reconcileMutation.data.conflict}</p>
            </div>
          )}
          <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">推奨運用</p>
            <p className="mt-1">旧リストは `refresh`、新規獲得リストは `append` に固定すると履歴管理が崩れません。</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <h2 className="font-semibold text-slate-900">実行可能サマリー</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">総件数</p>
              <p className="text-2xl font-bold">{executableSummary?.total || 0}</p>
            </div>
            <div className="rounded-lg bg-green-50 p-3">
              <p className="text-xs text-green-700">架電可</p>
              <p className="text-2xl font-bold text-green-700">{executableSummary?.readyForCall || 0}</p>
            </div>
            <div className="rounded-lg bg-blue-50 p-3">
              <p className="text-xs text-blue-700">FAX可</p>
              <p className="text-2xl font-bold text-blue-700">{executableSummary?.readyForFax || 0}</p>
            </div>
            <div className="rounded-lg bg-amber-50 p-3">
              <p className="text-xs text-amber-700">要肉付け</p>
              <p className="text-2xl font-bold text-amber-700">{executableSummary?.masterOnly || 0}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { setReadyPreset("ALL"); setHasPhoneOnly(true); }}
              className={`px-3 py-1.5 rounded text-xs border ${readyPreset === "ALL" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-300"}`}
            >
              全体
            </button>
            <button
              type="button"
              onClick={() => { setReadyPreset("CALL"); setHasPhoneOnly(false); setPage(1); }}
              className={`px-3 py-1.5 rounded text-xs border ${readyPreset === "CALL" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-300"}`}
            >
              架電可
            </button>
            <button
              type="button"
              onClick={() => { setReadyPreset("FAX"); setHasPhoneOnly(false); setPage(1); }}
              className={`px-3 py-1.5 rounded text-xs border ${readyPreset === "FAX" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-300"}`}
            >
              FAX可
            </button>
            <button
              type="button"
              onClick={() => { setReadyPreset("MASTER"); setHasPhoneOnly(false); setPage(1); }}
              className={`px-3 py-1.5 rounded text-xs border ${readyPreset === "MASTER" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-300"}`}
            >
              要肉付け
            </button>
          </div>
          <div className="space-y-2">
            {(executableSummary?.recommendations || []).map((item) => (
              <div key={item} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{item}</div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">再精査候補</h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">古さ基準</span>
              <select
                value={staleDays}
                onChange={(e) => setStaleDays(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value="180">180日</option>
                <option value="365">365日</option>
                <option value="540">540日</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">候補件数</p>
              <p className="text-2xl font-bold">
                {refreshCandidates?.summary.reduce((sum, row) => sum + row._count._all, 0) || 0}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">ソース数</p>
              <p className="text-2xl font-bold">
                {new Set((refreshCandidates?.summary || []).map((row) => row.listSource || "-")).size}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">推奨</p>
              <p className="text-sm font-medium mt-2">再取込 or 架電棚卸し</p>
            </div>
          </div>
          <div className="space-y-2">
            {(refreshCandidates?.recommendedActions || []).map((action) => (
              <div key={action} className="text-sm text-slate-700 rounded-lg bg-blue-50 px-3 py-2">{action}</div>
            ))}
          </div>
          <div className="space-y-2">
            {(refreshCandidates?.samples || []).slice(0, 5).map((sample) => (
              <div key={sample.id} className="rounded-lg border p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <strong className="text-slate-900">{sample.company}</strong>
                  <span className={`px-2 py-0.5 rounded text-xs ${freshnessColors[sample.freshnessStatus]}`}>
                    {freshnessLabels[sample.freshnessStatus]}
                  </span>
                </div>
                <p className="text-slate-600 mt-1">{sample.listSource} / {sample.listVersion}</p>
                <p className="text-xs text-slate-500 mt-1">
                  更新日 {compactDate(sample.sourceUpdatedAt)} / 確認日 {compactDate(sample.lastVerifiedAt)}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border p-4 space-y-4">
          <h2 className="font-semibold text-slate-900">架電優先キュー</h2>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">総候補</p>
              <p className="text-2xl font-bold">{callQueue?.totalCandidates || 0}</p>
            </div>
            <div className="rounded-lg bg-green-50 p-3">
              <p className="text-xs text-green-700">架電可能</p>
              <p className="text-2xl font-bold text-green-700">{callQueue?.eligibleCount || 0}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">除外</p>
              <p className="text-2xl font-bold">{callQueue?.excludedCount || 0}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(callQueue?.bucketCounts || {}).map(([bucket, count]) => (
              <span key={bucket} className="rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700">
                {bucket}: {count}
              </span>
            ))}
          </div>
          <div className="space-y-2">
            {(callQueue?.items || []).slice(0, 8).map((item) => (
              <div key={item.prospectId} className="rounded-lg border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-900">{item.company}</p>
                    <p className="text-xs text-slate-500 mt-1">{item.bucketLabel}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs ${verificationColors[item.verificationStatus]}`}>
                    {verificationLabels[item.verificationStatus]}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span>優先度 {item.priorityScore}</span>
                  <span>{item.phone || "-"}</span>
                  <span>{freshnessLabels[item.freshnessStatus]}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-semibold text-slate-900">見込みリスト一覧</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative min-w-60">
              <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={companySearch}
                onChange={(e) => {
                  setCompanySearch(e.target.value);
                  setPage(1);
                }}
                className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
                placeholder="会社名で検索"
              />
            </div>
            <input
              type="text"
              value={listSource}
              onChange={(e) => {
                setListSource(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-2 text-sm"
              placeholder="listSource"
            />
            <input
              type="text"
              value={listVersion}
              onChange={(e) => {
                setListVersion(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-2 text-sm"
              placeholder="listVersion"
            />
            <select
              value={freshnessStatus}
              onChange={(e) => {
                setFreshnessStatus(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">鮮度すべて</option>
              <option value="FRESH">FRESH</option>
              <option value="AGING">AGING</option>
              <option value="STALE">STALE</option>
            </select>
            <select
              value={verificationStatus}
              onChange={(e) => {
                setVerificationStatus(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">確認状況すべて</option>
              <option value="UNVERIFIED">UNVERIFIED</option>
              <option value="VERIFIED">VERIFIED</option>
              <option value="UNREACHABLE">UNREACHABLE</option>
              <option value="REFUSED">REFUSED</option>
              <option value="INVALID">INVALID</option>
            </select>
            <select
              value={industryFit}
              onChange={(e) => {
                setIndustryFit(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">業種適合すべて</option>
              <option value="HIGH,MEDIUM">HIGH + MEDIUM</option>
              <option value="HIGH">{industryFitLabels.HIGH}</option>
              <option value="MEDIUM">{industryFitLabels.MEDIUM}</option>
              <option value="LOW">{industryFitLabels.LOW}</option>
              <option value="EXCLUDE">{industryFitLabels.EXCLUDE}</option>
              <option value="UNKNOWN">{industryFitLabels.UNKNOWN}</option>
            </select>
            <input
              type="text"
              value={prefecture}
              onChange={(e) => {
                setPrefecture(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-2 text-sm"
              placeholder="都道府県"
            />
            <select
              value={hasLastOrder}
              onChange={(e) => {
                setHasLastOrder(e.target.value as "" | "true" | "false");
                setPage(1);
              }}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">受注履歴すべて</option>
              <option value="false">受注履歴なし</option>
              <option value="true">受注履歴あり</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={untouchedOnly}
                onChange={(e) => {
                  setUntouchedOnly(e.target.checked);
                  setPage(1);
                }}
              />
              未接触のみ
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={hasPhoneOnly}
                onChange={(e) => {
                  setHasPhoneOnly(e.target.checked);
                  setPage(1);
                }}
              />
              電話ありのみ
            </label>
            {data && <span className="text-sm text-slate-500">{data.total}件</span>}
          </div>
        </div>

        {isLoading ? (
          <p className="text-slate-500">読み込み中...</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">会社名</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">電話</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">メール</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">リスト</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">鮮度</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">確認</th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600">最終更新</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data?.data.map((prospect) => (
                    <tr key={prospect.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2">
                        <div>
                          <p className="font-medium text-slate-900">{prospect.company}</p>
                          <p className="text-xs text-slate-500">{prospect.contactName || prospect.prefecture || "-"}</p>
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-600">{prospect.phone || prospect.cell || "-"}</td>
                      <td className="px-4 py-2 text-slate-600">{prospect.email || "-"}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">{prospect.listSource} / {prospect.listVersion}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${freshnessColors[prospect.freshnessStatus]}`}>
                          {freshnessLabels[prospect.freshnessStatus]}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${verificationColors[prospect.verificationStatus]}`}>
                          {verificationLabels[prospect.verificationStatus]}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-slate-600">{compactDate(prospect.sourceUpdatedAt || prospect.updatedAt)}</td>
                    </tr>
                  ))}
                  {data?.data.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                        条件に一致する見込みリストがありません
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {data && data.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  {(page - 1) * data.limit + 1}-{Math.min(page * data.limit, data.total)} / {data.total}件
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    disabled={page <= 1}
                    className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setPage((current) => Math.min(data.totalPages, current + 1))}
                    disabled={page >= data.totalPages}
                    className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
